import { createContext, useCallback, useContext } from 'react';
import { isFeatureEnabled } from '@vp/ab-reader';
import { getQueryParams } from 'src/utils/QueryStringUtil';
import { parseSerializedEntryValue, readOverrides } from './util';

/**
 * Safely parse persisted developer overrides from localStorage and return a boolean override
 * value for `flagName` if present. This helper intentionally performs a minimal, in-place parse
 * so we don't have to import the heavier debug util (and its cookie/query helpers) into the
 * production bundle used by the app.
 *
 * Precedence implemented here (highest -> lowest):
 * 1. Query param (?flagName=true/false) for quick ad-hoc forcing during development
 * 2. Explicit persisted overrides stored under `FEATURE_FLAG_OVERRIDES_KEY`
 */
function readLocalOverride(flagName: string): boolean | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    // 0. Query param (?flagName=true/false) has the highest precedence for quick ad-hoc forcing
    const params = getQueryParams();
    const qpVal = params[flagName];
    if (qpVal === 'true') {
      return true;
    }
    if (qpVal === 'false') {
      return false;
    }

    // 1. Explicit developer overrides
    const overrideEntries = readOverrides();
    const overrideEntry = overrideEntries.find(e => e.startsWith(flagName + '^'));
    const parsedOverride = parseSerializedEntryValue(overrideEntry);
    if (parsedOverride !== undefined) {
      return parsedOverride;
    }

    return undefined;
  } catch {
    return undefined;
  }
}

export interface FeatureGateStatus {
  isEnabled: () => boolean;
  loading: boolean;
}

interface FeatureGateContextProps {
  isInitialized: boolean;
}

export const FeatureGatesContext = createContext<FeatureGateContextProps>({
  isInitialized: false,
});

export const useFeatureGate = (flagName: string): FeatureGateStatus => {
  const gatesContext = useContext(FeatureGatesContext);
  return {
    isEnabled: useCallback(() => {
      // Local override (developer-set) takes precedence while debugging locally.
      const override = readLocalOverride(flagName);
      if (override !== undefined) {
        return override;
      }
      // Fall back to remote/Statsig evaluation only after initialization.
      return gatesContext.isInitialized ? isFeatureEnabled(flagName) : false;
    }, [flagName, gatesContext.isInitialized]),
    loading: !gatesContext.isInitialized,
  };
};
